import { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    MenuItem,
    Stack,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/en-au';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import { apiFetch } from '../../lib/api';
import { addDays, type IsoDate } from '../../lib/dates';
import { SectionLabel, PanelCard } from '../primitives';
import { dmy, hrs, LEAVE_TYPES, type Entry, type Lookups, type TimesheetStaff } from './types';

/**
 * Add or edit one timesheet entry.
 *
 * The legacy screen did this inline in the table (Backend_timesheet
 * get_timesheet_edit / get_timesheet_update), with a blank row created first by
 * "Manual Entry". Same fields, same rules, in the dialog style of the Jobs page:
 *
 *   * Job work picks a Client, then one of its active Job Addresses. Both are
 *     required unless the row is Other (leave) — "Please Select Client".
 *   * Choosing Other sets 07:00–15:30, as the legacy job_other() did.
 *   * Lunch is only offered for staff with the Lunch payroll category, and the
 *     API refuses a second lunch on the same day.
 *   * Hours and overtime are worked out by the API from the times. Where the
 *     times are not the truth, an admin adjusts the hours instead.
 *   * A finish earlier than the start is the next morning — a night shift.
 *     The form sends that as finishDate; without it, saving a 20:57–05:30
 *     shift wiped its next-day finish and the hours fell to zero.
 */

/** "HH:MM" as a Dayjs the pickers can hold. The date part is irrelevant. */
function timeValue(t: string): Dayjs | null {
    return t === '' ? null : dayjs(`2000-01-01T${t}`);
}

/**
 * A time field that drops down an hours | minutes list when clicked anywhere,
 * on touch screens too (no clock dial). Typing straight into it still works.
 * Opening it while blank fills in openAt, so a still-clocked-on entry is only
 * given a finish when someone actually opens the finish dropdown.
 */
function TimeDropdown({
    label,
    value,
    onChange,
    helperText,
    openAt,
}: {
    readonly label: string;
    readonly value: string;
    readonly onChange: (t: string) => void;
    readonly helperText: string;
    readonly openAt?: string;
}): React.JSX.Element {
    const [open, setOpen] = useState(false);
    const picked = useMemo(() => timeValue(value), [value]);
    function show(): void {
        if (value === '' && openAt !== undefined) {
            onChange(openAt);
        }
        setOpen(true);
    }
    return (
        <DesktopTimePicker
            label={label}
            ampm={false}
            views={['hours', 'minutes']}
            timeSteps={{ minutes: 5 }}
            open={open}
            onOpen={show}
            onClose={() => {
                setOpen(false);
            }}
            value={picked}
            onChange={(v) => {
                if (v === null) {
                    onChange('');
                } else if (v.isValid()) {
                    onChange(v.format('HH:mm'));
                }
            }}
            slotProps={{
                textField: {
                    size: 'small',
                    fullWidth: true,
                    helperText,
                    onClick: () => {
                        if (!open) {
                            show();
                        }
                    },
                    sx: { cursor: 'pointer' },
                },
                actionBar: { actions: ['clear', 'accept'] },
                popper: { placement: 'bottom-start' },
            }}
        />
    );
}

type Kind = 'job' | 'other';

interface Props {
    readonly open: boolean;
    readonly entry: Entry | null;
    readonly staff: readonly TimesheetStaff[];
    readonly lookups: Lookups | null;
    readonly defaultStaffId: number | null;
    readonly defaultDate: IsoDate;
    readonly onClose: () => void;
    readonly onSaved: (message: string) => void;
    readonly onDeleteRequest: (entry: Entry) => void;
}

function minutes(t: string): number | null {
    const m = /^(\d{1,2}):(\d{2})/.exec(t);
    return m === null ? null : Number(m[1]) * 60 + Number(m[2]);
}

export function EntryDialog({
    open,
    entry,
    staff,
    lookups,
    defaultStaffId,
    defaultDate,
    onClose,
    onSaved,
    onDeleteRequest,
}: Props): React.JSX.Element {
    const [staffId, setStaffId] = useState('');
    const [workDate, setWorkDate] = useState('');
    const [kind, setKind] = useState<Kind>('job');
    const [clientId, setClientId] = useState('');
    const [jobId, setJobId] = useState('');
    const [leaveType, setLeaveType] = useState('');
    const [leaveHours, setLeaveHours] = useState('');
    const [startTime, setStartTime] = useState('');
    const [finishTime, setFinishTime] = useState('');
    const [lunch, setLunch] = useState(false);
    const [note, setNote] = useState('');
    const [adjusting, setAdjusting] = useState(false);
    const [adjustedHours, setAdjustedHours] = useState('');
    const [adjustedReason, setAdjustedReason] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) {
            return;
        }
        setError(null);
        if (entry === null) {
            setStaffId(defaultStaffId === null ? '' : String(defaultStaffId));
            setWorkDate(defaultDate);
            setKind('job');
            setClientId('');
            setJobId('');
            setLeaveType('');
            setLeaveHours('');
            setStartTime('07:00');
            setFinishTime('15:30');
            setLunch(false);
            setNote('');
            setAdjusting(false);
            setAdjustedHours('');
            setAdjustedReason('');
            return;
        }
        setStaffId(entry.staffId === null ? '' : String(entry.staffId));
        setWorkDate(entry.workDate);
        const isOther = entry.leaveType !== null || (entry.jobId === null && entry.jobOther !== null);
        setKind(isOther ? 'other' : 'job');
        setClientId(entry.clientId === null ? '' : String(entry.clientId));
        setJobId(entry.jobId === null ? '' : String(entry.jobId));
        setLeaveType(entry.leaveType ?? '');
        setLeaveHours(entry.leaveHours === 0 ? '' : String(entry.leaveHours));
        setStartTime(entry.startTime?.slice(0, 5) ?? '');
        setFinishTime(entry.finishTime?.slice(0, 5) ?? '');
        setLunch(entry.lunchTaken);
        setNote(entry.note ?? '');
        setAdjusting(entry.adjustedHours !== null);
        setAdjustedHours(entry.adjustedHours === null ? '' : String(entry.adjustedHours));
        setAdjustedReason(entry.adjustedReason ?? '');
    }, [open, entry, defaultStaffId, defaultDate]);

    const person = staff.find((s) => String(s.staffId) === staffId);
    // Offered to staff with the Lunch category (legacy work_category 8), and kept
    // visible on an entry that already has one so it can be taken off.
    const lunchAllowed = person?.payrollKinds.includes('lunch') === true || entry?.lunchTaken === true;

    const jobsForClient = useMemo(() => {
        const all = lookups?.jobs ?? [];
        const list = clientId === '' ? [] : all.filter((j) => String(j.clientId) === clientId);
        // An entry on a job that has since gone Inactive keeps its job selectable.
        if (
            entry !== null &&
            entry.jobId !== null &&
            !list.some((j) => j.id === entry.jobId) &&
            String(entry.clientId) === clientId
        ) {
            return [
                ...list,
                {
                    id: entry.jobId,
                    jobNumber: entry.jobNumber,
                    address: entry.jobAddress,
                    suburb: entry.jobSuburb,
                    clientId: entry.clientId,
                },
            ];
        }
        return list;
    }, [lookups, clientId, entry]);

    const clients = useMemo(() => {
        const list = lookups?.clients ?? [];
        if (entry !== null && entry.clientId !== null && !list.some((c) => c.id === entry.clientId)) {
            return [...list, { id: entry.clientId, name: entry.clientName ?? `Client ${String(entry.clientId)}` }];
        }
        return list;
    }, [lookups, entry]);

    /** The same arithmetic the API does before overtime, as a preview. */
    const preview = useMemo(() => {
        const s = minutes(startTime);
        const f = minutes(finishTime);
        if (s === null || f === null || f === s) {
            return null;
        }
        const span = f > s ? f - s : f + 24 * 60 - s;
        return Math.max(0, span / 60 - (kind === 'job' && lunch ? 0.5 : 0));
    }, [startTime, finishTime, lunch, kind]);

    /** Set when the finish is the morning after the work date. */
    const finishNextDay = useMemo(() => {
        const s = minutes(startTime);
        const f = minutes(finishTime);
        return s !== null && f !== null && f < s && workDate !== '' ? addDays(workDate, 1) : null;
    }, [startTime, finishTime, workDate]);

    // Memoised so a half-typed value does not hand the pickers a new object
    // every render, which would wipe what is being typed.
    const workDateValue = useMemo(() => (workDate === '' ? null : dayjs(workDate)), [workDate]);

    function chooseKind(next: Kind | null): void {
        if (next === null) {
            return;
        }
        setKind(next);
        if (next === 'other') {
            setClientId('');
            setJobId('');
            setLunch(false);
            setAdjusting(false);
            setStartTime('07:00');
            setFinishTime('15:30');
            if (leaveHours === '') {
                setLeaveHours('8');
            }
        } else {
            setLeaveType('');
        }
    }

    function save(): void {
        if (staffId === '') {
            setError('Choose a staff member');
            return;
        }
        if (kind === 'job' && (clientId === '' || jobId === '')) {
            setError(clientId === '' ? 'Select a client' : 'Select a job address');
            return;
        }
        if (kind === 'other' && leaveType === '') {
            setError('Select what the time is under Other');
            return;
        }
        setSaving(true);
        setError(null);
        const body: Record<string, unknown> = {
            staffId: Number(staffId),
            workDate,
            startTime: startTime === '' ? null : startTime,
            finishTime: finishTime === '' ? null : finishTime,
            finishDate: finishTime === '' ? null : (finishNextDay ?? workDate),
            lunchTaken: kind === 'job' && lunch,
            jobId: kind === 'job' ? Number(jobId) : null,
            leaveType: kind === 'other' ? leaveType : null,
            leaveHours: kind === 'other' && leaveHours !== '' ? Number(leaveHours) : 0,
            note: note === '' ? null : note,
            ...(kind === 'job' && adjusting
                ? {
                      adjustedHours: adjustedHours === '' ? null : Number(adjustedHours),
                      adjustedReason: adjustedReason === '' ? null : adjustedReason,
                  }
                : entry !== null && entry.adjustedHours !== null
                  ? { adjustedHours: null, adjustedReason: null }
                  : {}),
        };
        const path = entry === null ? '/timesheets' : `/timesheets/${String(entry.id)}`;
        apiFetch<unknown>(path, { method: entry === null ? 'POST' : 'PATCH', body: JSON.stringify(body) })
            .then(() => {
                onSaved(entry === null ? 'Entry added' : 'Entry updated');
            })
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not save');
            })
            .finally(() => {
                setSaving(false);
            });
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-au">
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth slotProps={{ paper: { elevation: 1 } }}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {entry === null ? 'New Entry' : 'Edit Entry'}
                    {entry !== null ? (
                        <Typography component="span" sx={{ color: 'text.secondary', fontSize: '0.95rem' }}>
                            {entry.staffName} · {dmy(entry.workDate)}
                        </Typography>
                    ) : null}
                    <Box sx={{ flex: 1 }} />
                    <IconButton onClick={onClose} size="small" disabled={saving} aria-label="Close">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    {error !== null ? (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    ) : null}
                    {entry?.invoiced === true ? (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            This entry has been invoiced. Changing it does not change the invoice.
                        </Alert>
                    ) : null}

                    <Stack gap={2.5}>
                        <PanelCard>
                            <SectionLabel>Entry</SectionLabel>
                            <Stack gap={1.5}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                                    <TextField
                                        select
                                        label="Staff member"
                                        size="small"
                                        fullWidth
                                        value={staffId}
                                        disabled={entry !== null}
                                        onChange={(e) => {
                                            setStaffId(e.target.value);
                                        }}
                                    >
                                        {staff.map((s) => (
                                            <MenuItem key={s.staffId} value={String(s.staffId)}>
                                                {s.name}
                                            </MenuItem>
                                        ))}
                                        {entry !== null && person === undefined && entry.staffId !== null ? (
                                            <MenuItem value={String(entry.staffId)}>{entry.staffName}</MenuItem>
                                        ) : null}
                                    </TextField>
                                    <DatePicker
                                        label="Date"
                                        format="ddd D MMM YYYY"
                                        value={workDateValue}
                                        onChange={(v) => {
                                            if (v?.isValid() === true) {
                                                setWorkDate(v.format('YYYY-MM-DD'));
                                            }
                                        }}
                                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                    />
                                </Stack>

                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    color="primary"
                                    value={kind}
                                    onChange={(_e, v: Kind | null) => {
                                        chooseKind(v);
                                    }}
                                >
                                    <ToggleButton value="job">Job</ToggleButton>
                                    <ToggleButton value="other">Other (leave, trade school)</ToggleButton>
                                </ToggleButtonGroup>

                                {kind === 'job' ? (
                                    <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                                        <TextField
                                            select
                                            label="Client"
                                            size="small"
                                            fullWidth
                                            value={clientId}
                                            onChange={(e) => {
                                                setClientId(e.target.value);
                                                setJobId('');
                                            }}
                                        >
                                            {clients.map((c) => (
                                                <MenuItem key={c.id} value={String(c.id)}>
                                                    {c.name}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                        <TextField
                                            select
                                            label="Job address"
                                            size="small"
                                            fullWidth
                                            value={jobId}
                                            disabled={clientId === ''}
                                            helperText={
                                                clientId !== '' && jobsForClient.length === 0
                                                    ? 'This client has no active jobs'
                                                    : ' '
                                            }
                                            onChange={(e) => {
                                                setJobId(e.target.value);
                                            }}
                                        >
                                            {jobsForClient.map((j) => (
                                                <MenuItem key={j.id} value={String(j.id)}>
                                                    {[j.address, j.suburb]
                                                        .filter((p) => p !== null && p !== '')
                                                        .join(', ')}
                                                    {j.jobNumber !== null ? ` · #${String(j.jobNumber)}` : ''}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Stack>
                                ) : (
                                    <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                                        <TextField
                                            select
                                            label="Other"
                                            size="small"
                                            fullWidth
                                            value={leaveType}
                                            onChange={(e) => {
                                                setLeaveType(e.target.value);
                                            }}
                                        >
                                            {LEAVE_TYPES.map((l) => (
                                                <MenuItem key={l.id} value={l.id}>
                                                    {l.label}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                        <TextField
                                            label="Hours"
                                            size="small"
                                            fullWidth
                                            value={leaveHours}
                                            onChange={(e) => {
                                                setLeaveHours(e.target.value);
                                            }}
                                            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                                            helperText="Leave blank to use the start and finish times"
                                        />
                                    </Stack>
                                )}
                            </Stack>
                        </PanelCard>

                        <PanelCard>
                            <SectionLabel>Times</SectionLabel>
                            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} alignItems={{ sm: 'center' }}>
                                <TimeDropdown
                                    label="Start time"
                                    value={startTime}
                                    onChange={setStartTime}
                                    helperText=" "
                                />
                                <TimeDropdown
                                    label="Finish time"
                                    openAt="15:30"
                                    value={finishTime}
                                    onChange={setFinishTime}
                                    helperText={
                                        finishTime === ''
                                            ? 'Blank = still clocked on'
                                            : finishNextDay !== null
                                              ? `Next day — ${dmy(finishNextDay)}`
                                              : ' '
                                    }
                                />
                                {kind === 'job' && lunchAllowed ? (
                                    <FormControlLabel
                                        sx={{ minWidth: 170 }}
                                        control={
                                            <Switch
                                                checked={lunch}
                                                onChange={(e) => {
                                                    setLunch(e.target.checked);
                                                }}
                                            />
                                        }
                                        label="Lunch (−0.5 h)"
                                    />
                                ) : null}
                            </Stack>
                            <Typography sx={{ mt: 1, fontSize: '0.8rem', color: 'text.secondary' }}>
                                {preview === null
                                    ? 'Hours are worked out from the times when saved.'
                                    : `${preview.toFixed(2)} hours from these times. Overtime is worked out when saved.`}
                            </Typography>
                        </PanelCard>

                        {kind === 'job' ? (
                            <PanelCard>
                                <Stack direction="row" alignItems="center" gap={1}>
                                    <Box sx={{ flex: 1 }}>
                                        <SectionLabel>Adjust hours</SectionLabel>
                                    </Box>
                                    <Switch
                                        checked={adjusting}
                                        onChange={(e) => {
                                            setAdjusting(e.target.checked);
                                        }}
                                    />
                                </Stack>
                                {adjusting ? (
                                    <Stack gap={1}>
                                        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                                            <TextField
                                                label="Hours"
                                                size="small"
                                                fullWidth
                                                value={adjustedHours}
                                                onChange={(e) => {
                                                    setAdjustedHours(e.target.value);
                                                }}
                                                slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                                            />
                                            <TextField
                                                label="Reason"
                                                size="small"
                                                fullWidth
                                                value={adjustedReason}
                                                onChange={(e) => {
                                                    setAdjustedReason(e.target.value);
                                                }}
                                            />
                                        </Stack>
                                        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                            Use when the times are not the hours worked. Overtime is worked out from
                                            this figure, and the entry is marked as adjusted.
                                        </Typography>
                                    </Stack>
                                ) : (
                                    <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                        {entry !== null && entry.adjustedHours !== null
                                            ? `Turning this off goes back to the ${hrs(entry.calculatedHours)} hours from the times.`
                                            : 'Hours come from the start and finish times.'}
                                    </Typography>
                                )}
                            </PanelCard>
                        ) : null}

                        <PanelCard>
                            <SectionLabel>Note</SectionLabel>
                            <TextField
                                size="small"
                                fullWidth
                                multiline
                                minRows={2}
                                value={note}
                                placeholder="The staff member's note for this shift"
                                onChange={(e) => {
                                    setNote(e.target.value);
                                }}
                            />
                            {entry !== null && entry.editedByName !== null && entry.editedAt !== null ? (
                                <Typography sx={{ mt: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
                                    Last edited by {entry.editedByName} on {dmy(entry.editedAt.slice(0, 10))}
                                </Typography>
                            ) : null}
                        </PanelCard>
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    {entry !== null ? (
                        <Button
                            color="error"
                            startIcon={<DeleteOutlineIcon />}
                            disabled={saving}
                            onClick={() => {
                                onDeleteRequest(entry);
                            }}
                            sx={{ mr: 'auto' }}
                        >
                            Delete Entry
                        </Button>
                    ) : null}
                    <Button onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={save}
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    >
                        {saving ? 'Saving...' : entry === null ? 'Save Entry' : 'Update Entry'}
                    </Button>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
}
